import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icone } from "./Icone.js";
import type { Glifo } from "./glifos.js";
import { IconeAbrirMenu, IconeCriar, IconeLinhaHorizontal, IconeMais } from "./icones.js";

/**
 * Os controles do handoff de UI/UX (ADR 039): seletor segmentado, interruptor,
 * contador e o menu ⋯ com confirmação.
 *
 * Nenhum deles escreve atributo `style`. O polegar do seletor se posiciona por
 * duas custom properties escritas pelo CSSOM (`style.setProperty`), que a CSP
 * `style-src 'self'` não bloqueia — ela barra estilo no markup, não a API.
 */

// -------------------------------------------------------- seletor segmentado

export interface OpcaoSegmentada<T extends string> {
  valor: T;
  rotulo: string;
}

/**
 * Um grupo de opções exclusivas com o polegar creme deslizando entre elas.
 * É um `radiogroup`: setas trocam a opção, e só a escolhida entra na ordem de
 * tabulação.
 */
export function SeletorSegmentado<T extends string>({
  opcoes,
  valor,
  aoMudar,
  rotulo,
  className,
}: {
  opcoes: readonly OpcaoSegmentada<T>[];
  valor: T;
  aoMudar: (v: T) => void;
  rotulo: string;
  className?: string;
}) {
  const trilho = useRef<HTMLDivElement>(null);
  const [pronto, setPronto] = useState(false);

  useLayoutEffect(() => {
    const el = trilho.current;
    if (!el) return;
    const posicionar = () => {
      const ativo = el.querySelector<HTMLElement>('[aria-checked="true"]');
      if (!ativo) return;
      el.style.setProperty("--polegar-x", `${ativo.offsetLeft}px`);
      el.style.setProperty("--polegar-l", `${ativo.offsetWidth}px`);
      setPronto(true);
    };
    posicionar();
    const observador = new ResizeObserver(posicionar);
    observador.observe(el);
    return () => observador.disconnect();
  }, [valor, opcoes]);

  const aoTeclar = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = opcoes.findIndex((o) => o.valor === valor);
    let proximo = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") proximo = (i + 1) % opcoes.length;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") proximo = (i - 1 + opcoes.length) % opcoes.length;
    if (e.key === "Home") proximo = 0;
    if (e.key === "End") proximo = opcoes.length - 1;
    if (proximo < 0) return;
    e.preventDefault();
    aoMudar(opcoes[proximo]!.valor);
    trilho.current?.querySelectorAll<HTMLElement>('[role="radio"]')[proximo]?.focus();
  };

  return (
    <div
      ref={trilho}
      role="radiogroup"
      aria-label={rotulo}
      className={["segmentado", pronto ? "segmentado--pronto" : "", className ?? ""].filter(Boolean).join(" ")}
      onKeyDown={aoTeclar}
    >
      <span className="segmentado__polegar" aria-hidden="true" />
      {opcoes.map((o) => {
        const ativo = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={ativo}
            tabIndex={ativo ? 0 : -1}
            className="segmentado__opcao"
            onClick={() => aoMudar(o.valor)}
          >
            {o.rotulo}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- interruptor

/**
 * Liga/desliga: rótulo à esquerda, contagem colada nele, interruptor à
 * direita. A caixa nativa continua embaixo, invisível, com `role="switch"` —
 * é ela que dá teclado, formulário e leitor de tela de graça; o que se vê é o
 * trilho.
 */
export function Interruptor({
  rotulo,
  contagem,
  ajuda,
  ligado,
  desabilitado,
  aoMudar,
}: {
  rotulo: string;
  contagem?: number;
  ajuda?: ReactNode;
  ligado: boolean;
  desabilitado?: boolean;
  aoMudar: (v: boolean) => void;
}) {
  return (
    <label className={desabilitado ? "interruptor interruptor--desabilitado" : "interruptor"}>
      <span className="interruptor__texto">
        <span className="interruptor__rotulo">
          {rotulo}
          {contagem !== undefined && <span className="interruptor__conta dado"> · {contagem}</span>}
        </span>
        {ajuda && <span className="interruptor__ajuda">{ajuda}</span>}
      </span>
      <input
        type="checkbox"
        className="interruptor__caixa"
        role="switch"
        checked={ligado}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <span className="interruptor__trilho" aria-hidden="true" />
    </label>
  );
}

// ------------------------------------------------------------------- contador

/** Quantidade com − e + nas pontas. O número do meio continua digitável. */
export function Contador({
  valor,
  aoMudar,
  min,
  max,
  passo = 1,
  rotulo,
  id,
}: {
  valor: number;
  aoMudar: (v: number) => void;
  min: number;
  max: number;
  passo?: number;
  rotulo: string;
  id?: string;
}) {
  const gerado = useId();
  const idCampo = id ?? gerado;
  const limitar = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <div className="contador" role="group" aria-label={rotulo}>
      <button
        type="button"
        className="contador__passo"
        aria-label={`Diminuir ${rotulo.toLowerCase()}`}
        disabled={valor <= min}
        onClick={() => aoMudar(limitar(valor - passo))}
      >
        <Icone glifo={IconeLinhaHorizontal} papel="acao" tamanho="p" />
      </button>
      <input
        id={idCampo}
        className="contador__valor dado"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={passo}
        value={valor}
        aria-label={rotulo}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) aoMudar(limitar(n));
        }}
      />
      <button
        type="button"
        className="contador__passo"
        aria-label={`Aumentar ${rotulo.toLowerCase()}`}
        disabled={valor >= max}
        onClick={() => aoMudar(limitar(valor + passo))}
      >
        <Icone glifo={IconeCriar} papel="acao" tamanho="p" />
      </button>
    </div>
  );
}

// ------------------------------------------------------------ menu ⋯ de ações

export interface AcaoDoMenu {
  rotulo: string;
  aoEscolher: () => void;
  /** Ação destrutiva: pinta de erro e pede confirmação antes de rodar. */
  destrutiva?: boolean;
  /** A frase da confirmação. Obrigatória em ação destrutiva. */
  confirmacao?: string;
  desabilitada?: boolean;
}

/**
 * O menu ⋯: o lugar das ações que não são a principal da tela. Ação
 * destrutiva não roda no primeiro toque — o menu vira a pergunta, com
 * "Cancelar" focado (invariante 11). Exclusão que exige digitar o nome continua
 * com o diálogo próprio da tela; aqui o destrutivo pode só abrir esse diálogo.
 */
export function MenuDeAcoes({
  acoes,
  rotulo = "Mais ações",
  gatilho,
}: {
  acoes: readonly AcaoDoMenu[];
  rotulo?: string;
  /** Um gatilho com texto no lugar do ⋯, para menu que é ação da tela. */
  gatilho?: { texto: string; glifo?: Glifo };
}) {
  const [aberto, setAberto] = useState(false);
  const [confirmando, setConfirmando] = useState<AcaoDoMenu | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const idMenu = useId();

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: PointerEvent) => {
      if (!raiz.current?.contains(e.target as Node)) fechar();
    };
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") fechar();
    };
    document.addEventListener("pointerdown", fora);
    document.addEventListener("keydown", esc);
    raiz.current?.querySelector<HTMLElement>(".menu-acoes__item, .menu-acoes__cancelar")?.focus();
    return () => {
      document.removeEventListener("pointerdown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto, confirmando]);

  function fechar() {
    setAberto(false);
    setConfirmando(null);
  }

  return (
    <div className="menu-acoes" ref={raiz}>
      <button
        type="button"
        className={gatilho ? "menu-acoes__gatilho menu-acoes__gatilho--texto" : "menu-acoes__gatilho"}
        aria-label={gatilho ? undefined : rotulo}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={idMenu}
        onClick={() => (aberto ? fechar() : setAberto(true))}
      >
        {gatilho ? (
          <>
            {gatilho.glifo && <Icone glifo={gatilho.glifo} papel="acao" tamanho="p" />}
            {gatilho.texto}
            <Icone glifo={IconeAbrirMenu} papel="acao" tamanho="p" />
          </>
        ) : (
          <Icone glifo={IconeMais} papel="acao" />
        )}
      </button>
      {aberto && (
        <div className="menu-acoes__folha" id={idMenu} role={confirmando ? "alertdialog" : "menu"}>
          {confirmando ? (
            <div className="menu-acoes__confirmacao">
              <p className="menu-acoes__pergunta">{confirmando.confirmacao}</p>
              <div className="menu-acoes__botoes">
                <button
                  type="button"
                  className="botao botao--secundario botao--compacto menu-acoes__cancelar"
                  onClick={fechar}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="botao botao--perigo botao--compacto"
                  onClick={() => {
                    const acao = confirmando;
                    fechar();
                    acao.aoEscolher();
                  }}
                >
                  {confirmando.rotulo}
                </button>
              </div>
            </div>
          ) : (
            acoes.map((a) => (
              <button
                key={a.rotulo}
                type="button"
                role="menuitem"
                disabled={a.desabilitada}
                className={a.destrutiva ? "menu-acoes__item menu-acoes__item--destrutivo" : "menu-acoes__item"}
                onClick={() => {
                  if (a.destrutiva && a.confirmacao) {
                    setConfirmando(a);
                    return;
                  }
                  fechar();
                  a.aoEscolher();
                }}
              >
                {a.rotulo}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
